import { createRoute, redirect } from '@tanstack/react-router';
import { appRoute } from '@/features/dashboard/_root/routes/appRoute.tsx';
import { isMobileViewport } from '@/hooks/use-mobile.tsx';
import { queryClient } from '@/App.tsx';
import BookingDetailsPage from '@/features/dashboard/bookingChanges/pages/BookingDetailsPage.tsx';
import BookingTabMenu from '@/features/dashboard/bookingChanges/pages/BookingTabMenu.tsx';
import BookingBillTab from '@/features/dashboard/bookingChanges/pages/BookingBillTab.tsx';
import BookingConsentsTab from '@/features/dashboard/bookingChanges/pages/BookingConsentsTab.tsx';
import BookingInsurancesTab from '@/features/dashboard/bookingChanges/pages/BookingInsurancesTab.tsx';
import BookingMessagesTab from '@/features/dashboard/bookingChanges/pages/BookingMessagesTab.tsx';
import ChangeHistoryTab from '@/features/dashboard/bookingChanges/pages/ChangeHistoryTab.tsx';
import BookingDetailsSkeleton from '@/features/dashboard/bookingChanges/components/BookingDetailsSkeleton.tsx';
import DetailsErrorPage from '@/features/dashboard/bookingChanges/components/DetailsErrorPage.tsx';
import { detailsSearchSchema } from '@/features/dashboard/bookingChanges/routes/detailsSearch.ts';
import { listSearchSchema } from '@/components/table/routes/listSearchParams.ts';
import { BookingDetailsTab } from '@/features/dashboard/bookingChanges/routes/detailsTabs.ts';
import { getBookingBillQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingBill.ts';
import { getBookingDetailsQueryOptions } from '@/features/dashboard/bookings/hooks/useGetBookingDetails.ts';
import { getOpenDraftQueryOptions } from '@/features/dashboard/bookingChanges/hooks/useGetOpenDraft.ts';

/**
 * Ekran rezerwacji. Route nadrzędny to szkielet, zakładki mają własne segmenty
 * ścieżki; `?mode=` zostaje w adresie, bo tryb edycji dotyczy całego ekranu.
 *
 * Loader dociąga rachunek, metryczkę rezerwacji i otwarty szkic — trzy rzeczy,
 * bez których nagłówek nie ma czego pokazać; reszta kart dojeżdża już pod
 * szkieletami w panelu bocznym.
 */
export const bookingDetailsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/bookings/$bookingId',
  component: BookingDetailsPage,
  validateSearch: detailsSearchSchema,
  loader: async ({ params }) => {
    const { bookingId } = params;

    await Promise.all([
      queryClient.ensureQueryData(getBookingBillQueryOptions(bookingId)),
      queryClient.ensureQueryData(getBookingDetailsQueryOptions(bookingId)),
      queryClient.ensureQueryData(getOpenDraftQueryOptions(bookingId)),
    ]);
  },
  pendingComponent: BookingDetailsSkeleton,
  errorComponent: DetailsErrorPage,
});

/**
 * Adres bez segmentu zakładki — linki sprzed tej zmiany i wpisy z ręki.
 * Szeroko prowadzi na „Rezerwację”, poniżej `md` jest ekranem z kaflami.
 */
export const bookingDetailsIndexRoute = createRoute({
  getParentRoute: () => bookingDetailsRoute,
  path: '/',
  beforeLoad: ({ params, search }) => {
    if (isMobileViewport()) return;

    throw redirect({
      to: '/app/bookings/$bookingId/booking',
      params,
      search,
      replace: true,
    });
  },
  component: BookingTabMenu,
});

export const bookingDetailsBookingRoute = createRoute({
  getParentRoute: () => bookingDetailsRoute,
  path: BookingDetailsTab.BOOKING,
  component: BookingBillTab,
});

export const bookingDetailsConsentsRoute = createRoute({
  getParentRoute: () => bookingDetailsRoute,
  path: BookingDetailsTab.CONSENTS,
  component: BookingConsentsTab,
});

export const bookingDetailsInsurancesRoute = createRoute({
  getParentRoute: () => bookingDetailsRoute,
  path: BookingDetailsTab.INSURANCES,
  component: BookingInsurancesTab,
});

/** Jedyna zakładka będąca listą — obok `?mode=` z rodzica czyta więc wycinek listy z adresu. */
export const bookingDetailsMessagesRoute = createRoute({
  getParentRoute: () => bookingDetailsRoute,
  path: BookingDetailsTab.MESSAGES,
  component: BookingMessagesTab,
  validateSearch: listSearchSchema,
});

export const bookingDetailsHistoryRoute = createRoute({
  getParentRoute: () => bookingDetailsRoute,
  path: BookingDetailsTab.HISTORY,
  component: ChangeHistoryTab,
});
